import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { type ReactNode } from "react";
import { themeColors } from "../../app/layout/theme";

export interface FormDialogProps {
  open: boolean;

  title: string;

  subtitle?: string;

  onClose: () => void;

  onSave?: () => void;

  saveLabel?: string;
  saving?: boolean;
  saveDisabled?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  children: ReactNode;

  headerVariant?: "gradient" | "light";
}

export const FormDialog = ({
  open,
  title,
  subtitle,
  onClose,
  onSave,
  saveLabel = "Save",
  saving = false,
  saveDisabled = false,
  maxWidth = "sm",
  children,
  headerVariant = "light",
}: FormDialogProps) => {
  const isGradient = headerVariant === "gradient";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: "18px",
          pb: "14px",
          borderBottom: isGradient ? "none" : `1px solid ${themeColors.border}`,
          background: isGradient
            ? themeColors.primary
            : "linear-gradient(180deg, #fafbfc 0%, #f5f6f8 100%)",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "17px",
              color: isGradient ? "#fff" : themeColors.text.primary,
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              sx={{
                fontSize: "11px",
                mt: "3px",
                color: isGradient
                  ? "rgba(255,255,255,0.75)"
                  : themeColors.text.secondary,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          disabled={saving}
          sx={{
            color: isGradient
              ? "rgba(255,255,255,0.8)"
              : themeColors.text.secondary,
            "&:hover": {
              color: isGradient ? "#fff" : themeColors.text.primary,
              backgroundColor: isGradient
                ? "rgba(255,255,255,0.12)"
                : "rgba(0,0,0,0.04)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {children}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: `1px solid ${themeColors.borderLight}`,
          backgroundColor: "#fafafa",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={saving}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            borderColor: themeColors.border,
            color: themeColors.text.secondary,
            "&:hover": {
              borderColor: themeColors.primary,
              color: themeColors.primary,
            },
          }}
        >
          Cancel
        </Button>

        {onSave && (
          <Button
            onClick={onSave}
            variant="contained"
            disabled={saving || saveDisabled}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              minWidth: 80,
              backgroundColor: themeColors.primary,
              boxShadow: "0 4px 14px rgba(0,112,210,0.28)",
              transition: "background-color 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                backgroundColor: themeColors.primaryDark,
                boxShadow: "0 6px 18px rgba(0,112,210,0.36)",
              },
              "&.Mui-disabled": { opacity: 0.6 },
            }}
          >
            {saving ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              saveLabel
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
